/**
 * CRM API Module - Contact and Submission Management
 * Extracted from monolithic silo-api.ts for modular architecture
 */

import { type RequestFunction, ContactSubmissionStatusEnum } from './types'
import type {
  Contact,
  ContactCreateInput,
  ContactUpdateInput,
  ContactSubmission,
  ContactSubmissionUpdateInput,
  ContactNote,
  ContactNoteCreateInput,
  ContactBusiness,
  ContactBusinessCreateInput,
  ContactBusinessUpdateInput,
  User,
  PaginatedResponse,
  FilterParams,
  ContactInteractionHistory,
  ContactNotificationPreferences,
} from './types'

// ============================================================================
// STATUS MAPPING UTILITIES
// ============================================================================

/**
 * Maps frontend status values to backend status values
 * Frontend: 'new', 'in_progress', 'done', 'responded'
 * Backend: 'todo', 'doing', 'done'
 */
export const mapFrontendToBackendStatus = (frontendStatus: string): string => {
  const statusMap: Record<string, string> = {
    'new': ContactSubmissionStatusEnum.TODO,
    'in_progress': ContactSubmissionStatusEnum.DOING,
    'doing': ContactSubmissionStatusEnum.DOING, // Already backend format
    'done': ContactSubmissionStatusEnum.DONE,
    'responded': ContactSubmissionStatusEnum.DONE, // Map responded to done
    'todo': ContactSubmissionStatusEnum.TODO, // Already backend format
  }
  
  return statusMap[frontendStatus] || ContactSubmissionStatusEnum.TODO
}

/**
 * Maps backend status values to frontend status values
 * Backend: 'todo', 'doing', 'done'
 * Frontend: 'new', 'in_progress', 'done', 'responded'
 */
export const mapBackendToFrontendStatus = (backendStatus: string): string => {
  const statusMap: Record<string, string> = {
    [ContactSubmissionStatusEnum.TODO]: 'new',
    [ContactSubmissionStatusEnum.DOING]: 'in_progress',
    [ContactSubmissionStatusEnum.DONE]: 'done',
  }
  
  return statusMap[backendStatus] || 'new'
}

export class CrmApi {
  constructor(private request: RequestFunction) {}

  // ============================================================================
  // CONTACT MANAGEMENT
  // ============================================================================

  async getContacts(params?: FilterParams): Promise<PaginatedResponse<Contact>> {
    return this.request('/api/crm/contacts/', { params })
  }

  async getContact(id: number): Promise<Contact> {
    try {
      // Get contact data
      const contact = await this.request(`/api/crm/contacts/${id}/`)

      // If business information is missing and contact has business_id, try to populate it
      if (!contact.business && contact.business_id) {
        try {
          const businesses = await this.getContactBusinesses()
          const business = businesses.find((b: any) => b.id === contact.business_id)
          if (business) {
            contact.business = business
          }
        } catch (businessError) {
          console.warn('Failed to fetch business details for contact:', businessError)
        }
      }

      return contact
    } catch (error) {
      console.error('Error fetching contact:', error)
      throw error
    }
  }

  async createContact(data: ContactCreateInput): Promise<Contact> {
    return this.request('/api/crm/contacts/', { 
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateContact(id: number, data: ContactUpdateInput): Promise<Contact> {
    return this.request(`/api/crm/contacts/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  async deleteContact(id: number): Promise<void> {
    return this.request(`/api/crm/contacts/${id}/archive/`, {
      method: 'PATCH'
    })
  }

  async bulkUpdateContactStatus(ids: number[], status: string): Promise<void> {
    return this.request('/api/crm/contacts/bulk-update-status/', {
      method: 'POST',
      body: JSON.stringify({ ids, status })
    })
  }

  async getContactStatuses(): Promise<string[]> {
    return this.request('/api/crm/contacts/statuses/')
  }

  async getContactSources(): Promise<string[]> {
    return this.request('/api/crm/contacts/sources/')
  }

  async getContactPipelines(): Promise<string[]> {
    return this.request('/api/crm/contacts/pipelines/')
  }

  // ============================================================================
  // CONTACT PORTAL MANAGEMENT
  // ============================================================================

  async linkContactToUser(contactId: number, userId: number): Promise<Contact> {
    return this.request(`/api/crm/contacts/${contactId}/link-user/`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId })
    })
  }

  async linkExistingUserToContactAdmin(contactId: number, email: string): Promise<Contact> {
    return this.request(`/api/crm/contacts/${contactId}/link-portal-account/`, {
      method: 'POST',
      body: JSON.stringify({ email })
    })
  }

  async createPortalAccountAdmin(contactId: number, email: string): Promise<Contact> {
    return this.request(`/api/crm/contacts/${contactId}/create-portal-account/`, {
      method: 'POST',
      body: JSON.stringify({ email })
    })
  }

  async createPortalAccount(contactId: number, email: string): Promise<{ success: boolean; user: User }> {
    return this.request(`/api/crm/contacts/${contactId}/create-portal-account/`, {
      method: 'POST',
      body: JSON.stringify({ 
        email,
        send_invitation_email: true 
      })
    })
  }

  async sendPortalInvite(contactId: number, email?: string): Promise<{ 
    success: boolean
    message: string
    invite_status?: 'sent' | 'resent' | 'already_accepted'
    user?: User 
  }> {
    return this.request(`/api/crm/contacts/${contactId}/send-portal-invite/`, {
      method: 'POST',
      body: JSON.stringify({ 
        email: email,
        resend: false 
      })
    })
  }

  async resendPortalInvite(contactId: number): Promise<{ 
    success: boolean
    message: string
    invite_status: 'resent'
  }> {
    return this.request(`/api/crm/contacts/${contactId}/send-portal-invite/`, {
      method: 'POST',
      body: JSON.stringify({ 
        resend: true 
      })
    })
  }

  async getPortalInviteStatus(contactId: number): Promise<{
    has_account: boolean
    invite_sent: boolean
    invite_accepted: boolean
    user_email?: string
    invite_sent_at?: string
    invite_accepted_at?: string
  }> {
    try {
      // Use dedicated endpoint for portal status
      return this.request(`/api/crm/contacts/${contactId}/portal-status/`)
    } catch (error: any) {
      console.warn('Portal status endpoint failed, falling back to contact data extraction:', error)
      
      // Fallback: extract from contact data if dedicated endpoint fails
      try {
        const contact = await this.request(`/api/crm/contacts/${contactId}/`)
        
        return {
          has_account: !!contact.user_id || !!contact.portal_user,
          invite_sent: !!contact.portal_invite_sent || !!contact.invite_sent_at,
          invite_accepted: !!contact.portal_invite_accepted || !!contact.invite_accepted_at,
          user_email: contact.portal_user_email || contact.user?.email,
          invite_sent_at: contact.invite_sent_at,
          invite_accepted_at: contact.invite_accepted_at
        }
      } catch (fallbackError: any) {
        console.error('Both portal status endpoint and fallback failed:', fallbackError)
        return {
          has_account: false,
          invite_sent: false,
          invite_accepted: false
        }
      }
    }
  }

  async linkExistingUserToContact(contactId: number, userId: number): Promise<Contact> {
    return this.request(`/api/crm/contacts/${contactId}/link-user/`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId })
    })
  }

  async unlinkContactFromUser(contactId: number): Promise<Contact> {
    return this.request(`/api/crm/contacts/${contactId}/unlink-user/`, {
      method: 'POST'
    })
  }

  // ============================================================================
  // CONTACT NOTES
  // ============================================================================

  async getContactNotesAdmin(contactId: number, params?: FilterParams): Promise<PaginatedResponse<ContactNote>> {
    return this.request(`/api/crm/contacts/${contactId}/notes/`, { params })
  }

  async createContactNoteAdmin(data: { contact_id: number; text: string }): Promise<ContactNote> {
    const { contact_id, text } = data
    return this.request(`/api/crm/contacts/${contact_id}/notes/`, {
      method: 'POST',
      body: JSON.stringify({ text })
    })
  }

  async updateContactNoteAdmin(contactId: number, noteId: number, text: string): Promise<ContactNote> {
    return this.request(`/api/crm/contacts/${contactId}/notes/${noteId}/`, {
      method: 'PATCH',
      body: JSON.stringify({ text })
    })
  }

  async deleteContactNoteAdmin(contactId: number, noteId: number): Promise<void> {
    return this.request(`/api/crm/contacts/${contactId}/notes/${noteId}/archive/`, {
      method: 'PATCH',
      body: JSON.stringify({})
    })
  }

  async getContactNotes(contactId: number): Promise<ContactNote[]> {
    return this.request(`/api/crm/contacts/${contactId}/notes/`)
  }

  async createContactNote(contactId: number, data: ContactNoteCreateInput): Promise<ContactNote> {
    const { text } = data
    return this.request(`/api/crm/contacts/${contactId}/notes/`, {
      method: 'POST',
      body: JSON.stringify({ text })
    })
  }

  async updateContactNote(contactId: number, noteId: number, data: Partial<ContactNoteCreateInput>): Promise<ContactNote> {
    return this.request(`/api/crm/contacts/${contactId}/notes/${noteId}/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  async deleteContactNote(contactId: number, noteId: number): Promise<void> {
    return this.request(`/api/crm/contacts/${contactId}/notes/${noteId}/archive/`, {
      method: 'PATCH',
      body: JSON.stringify({})
    })
  }

  // ============================================================================
  // CONTACT BUSINESS
  // ============================================================================

  async getContactBusinesses(): Promise<ContactBusiness[]> {
    return this.request('/api/crm/contacts/businesses/')
  }

  async createContactBusiness(data: ContactBusinessCreateInput): Promise<ContactBusiness> {
    return this.request('/api/crm/contacts/businesses/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateContactBusiness(id: number, data: ContactBusinessUpdateInput): Promise<ContactBusiness> {
    return this.request(`/api/crm/contacts/businesses/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  async deleteContactBusiness(id: number): Promise<void> {
    return this.request(`/api/crm/contacts/businesses/${id}/archive/`, {
      method: 'PATCH',
      body: JSON.stringify({})
    })
  }

  // ============================================================================
  // CONTACT SUBMISSIONS
  // ============================================================================

  async getContactSubmissions(params?: FilterParams): Promise<PaginatedResponse<ContactSubmission>> {
    return this.request('/api/crm/contact-submissions/', { params })
  }

  async submitPublicContactForm(data: {
    name: string
    email: string
    request?: string
    phone?: string
    company?: string
    comments?: string
    subject?: string
    inquiry_type?: string
    company_address?: string
    company_city?: string
    company_state?: string
    company_zipcode?: string
    selected_products?: Array<{
      product_id: number
      product_name: string
      quantity: number
      unit: string
    }>
    metadata?: Record<string, any>
  }): Promise<ContactSubmission> {
    // This endpoint doesn't require authentication
    
    // Build request text based on whether this is a product inquiry or general contact
    let requestText = data.request || data.comments || ''
    
    if (data.selected_products && data.selected_products.length > 0) {
      // Format product inquiry
      const productList = data.selected_products.map(item => 
        `${item.product_name} - ${item.quantity}${item.unit}`
      ).join('\n')
      
      requestText = `Product Inquiry\n\nSelected Products:\n${productList}\n\nAdditional Comments:\n${data.comments || 'None'}`
    }
    
    // Extract company from either direct field or metadata
    const company = data.company || 
                   data.metadata?.company || 
                   data.metadata?.business_name || 
                   data.metadata?.businessName
    
    const payload = {
      name: data.name,
      email: data.email,
      request: requestText,
      // Add phone at top level if provided (backend might expect it here)
      ...(data.phone && { phone: data.phone }),
      // CRITICAL: Add company at root level for backend business creation
      ...(company && { company: company }),
      // Add new fields for partnership portal requests
      ...(data.subject && { subject: data.subject }),
      ...(data.inquiry_type && { inquiry_type: data.inquiry_type }),
      ...(data.company_address && { company_address: data.company_address }),
      ...(data.company_city && { company_city: data.company_city }),
      ...(data.company_state && { company_state: data.company_state }),
      ...(data.company_zipcode && { company_zipcode: data.company_zipcode }),
      metadata: {
        ...data.metadata,
        // Remove duplicate phone from metadata since it's already at root level
        ...(data.selected_products && {
          selected_products: data.selected_products,
          inquiry_type: 'product_inquiry'
        })
      }
    }
    
    console.log('Submitting contact form with payload:', payload)
    return this.request('/api/crm/contact-submission/', {
      method: 'POST',
      body: JSON.stringify(payload)
    })
  }

  async getContactSubmission(uuid: string): Promise<ContactSubmission> {
    return this.request(`/api/crm/contact-submissions/${uuid}/`)
  }

  async updateContactSubmission(uuid: string, data: ContactSubmissionUpdateInput): Promise<ContactSubmission> {
    const mappedData = {
      ...data,
      ...(data.status && { status: mapFrontendToBackendStatus(data.status) })
    }
    
    return this.request(`/api/crm/contact-submissions/${uuid}/`, {
      method: 'PATCH',
      body: JSON.stringify(mappedData)
    })
  }

  async deleteContactSubmission(uuid: string): Promise<void> {
    return this.request(`/api/crm/contact-submissions/${uuid}/archive/`, {
      method: 'PATCH',
      body: JSON.stringify({})
    })
  }

  async bulkUpdateContactSubmissionStatus(uuids: string[], status: string): Promise<void> {
    return this.request('/api/crm/contact-submissions/bulk-update-status/', {
      method: 'POST',
      body: JSON.stringify({ 
        uuids, 
        status: mapFrontendToBackendStatus(status) 
      })
    })
  }

  async bulkAssignContactSubmissions(uuids: string[], assignedTo: number): Promise<void> {
    return this.request('/api/crm/contact-submissions/bulk-assign/', {
      method: 'POST',
      body: JSON.stringify({ 
        uuids, 
        assigned_to: assignedTo 
      })
    })
  }

  async markContactSubmissionResponded(uuid: string): Promise<ContactSubmission> {
    return this.request(`/api/crm/contact-submissions/${uuid}/respond/`, {
      method: 'POST'
    })
  }

  async getContactSubmissionStats(): Promise<{
    total: number
    unread: number
    responded: number
    by_status: Record<string, number>
  }> {
    try {
      // FIXED: Correct endpoint path
      return this.request('/api/crm/contact-submissions/stats/')
    } catch (error: any) {
      console.warn('Contact submission stats endpoint failed, falling back to client calculation:', error)
      // Fallback to client-side calculation if the stats endpoint fails
      try {
        const response = await this.request<PaginatedResponse<ContactSubmission>>('/api/crm/contact-submissions/', { 
          params: { limit: 1000 }
        })
        
        const submissions = response.results || []
        
        const stats = {
          total: submissions.length,
          unread: 0,
          responded: 0,
          by_status: {} as Record<string, number>
        }
        
        submissions.forEach(submission => {
          const status = submission.status || 'todo'
          stats.by_status[status] = (stats.by_status[status] || 0) + 1
          
          if (status === 'todo') {
            stats.unread++
          } else if (status === 'done') {
            stats.responded++
          }
        })
        
        return stats
      } catch (fallbackError: any) {
        console.warn('Fallback stats calculation also failed:', fallbackError)
        return {
          total: 0,
          unread: 0,
          responded: 0,
          by_status: {}
        }
      }
    }
  }

  // Admin-specific contact submission methods
  async getContactSubmissionsAdmin(params?: FilterParams): Promise<PaginatedResponse<ContactSubmission>> {
    const response = await this.request<ContactSubmission[] | PaginatedResponse<ContactSubmission>>('/api/crm/contact-submissions/', { params })
    
    // Handle both array and paginated response formats
    if (Array.isArray(response)) {
      return {
        count: response.length,
        next: null,
        previous: null,
        total_pages: 1,
        current_page: 1,
        page_size: response.length,
        results: response
      }
    }
    
    return response
  }

  async getContactSubmissionAdmin(uuid: string): Promise<ContactSubmission> {
    return this.request(`/api/crm/contact-submissions/${uuid}/`)
  }

  async updateContactSubmissionAdmin(uuid: string, data: ContactSubmissionUpdateInput): Promise<ContactSubmission> {
    const mappedData = {
      ...data,
      ...(data.status && { status: mapFrontendToBackendStatus(data.status) })
    }
    
    return this.request(`/api/crm/contact-submissions/${uuid}/`, {
      method: 'PATCH',
      body: JSON.stringify(mappedData)
    })
  }

  async deleteContactSubmissionAdmin(uuid: string): Promise<void> {
    return this.request(`/api/crm/contact-submissions/${uuid}/archive/`, {
      method: 'PATCH',
      body: JSON.stringify({})
    })
  }

  async bulkUpdateContactSubmissionStatusAdmin(uuids: string[], status: string): Promise<{ updated_count: number }> {
    return this.request('/api/crm/contact-submissions/bulk-update-status/', {
      method: 'POST',
      body: JSON.stringify({
        uuids,
        status: mapFrontendToBackendStatus(status)
      })
    })
  }

  async bulkAssignContactSubmissionsAdmin(uuids: string[], assignedToId: number | null): Promise<{ updated_count: number }> {
    return this.request('/api/crm/contact-submissions/bulk-assign/', {
      method: 'POST',
      body: JSON.stringify({
        uuids,
        assigned_to_id: assignedToId
      })
    })
  }

  async markContactSubmissionRespondedAdmin(uuid: string): Promise<ContactSubmission> {
    return this.request(`/api/crm/contact-submissions/${uuid}/mark-responded/`, {
      method: 'POST',
      body: JSON.stringify({})
    })
  }

  async getContactSubmissionStatsAdmin(): Promise<{
    total: number
    by_status: Record<string, number>
    by_source: Record<string, number>
    recent_count: number
    response_rate: number
  }> {
    // FIXED: Correct endpoint path
    return this.request('/api/crm/contact-submissions/stats/')
  }

  // ============================================================================
  // ADDITIONAL CRM FEATURES
  // ============================================================================

  /**
   * Enhanced contact search with customer filtering
   */
  async getCustomerContacts(params?: { q?: string; page?: number }): Promise<PaginatedResponse<Contact>> {
    const searchParams = {
      type: 'customer',
      ...params
    }
    return this.request('/api/crm/contacts/', { params: searchParams })
  }

  /**
   * Contact addresses endpoint
   */
  async getContactAddresses(contactId: number): Promise<any[]> {
    return this.request(`/api/crm/contacts/${contactId}/addresses/`)
  }

  async createContactAddress(contactId: number, data: any): Promise<any> {
    return this.request(`/api/crm/contacts/${contactId}/addresses/`, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateContactAddress(contactId: number, addressId: number, data: any): Promise<any> {
    return this.request(`/api/crm/contacts/${contactId}/addresses/${addressId}/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  async deleteContactAddress(contactId: number, addressId: number): Promise<void> {
    const url = `/api/crm/contacts/${contactId}/addresses/${addressId}/`
    console.log(`[CRM API] Delete URL with trailing slash:`, url)
    return this.request(url, {
      method: 'DELETE'
    })
  }

  /**
   * Activity feed endpoint
   */
  async getActivityFeed(params?: { limit?: number; types?: string }): Promise<Array<{
    id: number
    type: 'order' | 'note' | 'contact'
    title: string
    description: string
    timestamp: string
    user?: string
    entity_id?: number
  }>> {
    const searchParams = {
      limit: 20,
      types: 'order,note,contact',
      ...params
    }
    return this.request('/api/crm/activity-feed/', { params: searchParams })
  }

  // ============================================================================
  // CONTACT INTERACTION HISTORY
  // ============================================================================

  /**
   * Get comprehensive contact interaction history
   * Returns chronological history of all interactions including:
   * - Orders placed
   * - Communications (from metadata/notes)  
   * - Status changes
   * - Contact submissions
   * - Notes added
   */
  async getContactInteractionHistory(
    contactId: number, 
    params?: {
      start_date?: string
      end_date?: string
      interaction_type?: 'order' | 'email' | 'phone' | 'meeting' | 'note'
      limit?: number
    }
  ): Promise<ContactInteractionHistory> {
    return this.request(`/api/crm/contacts/${contactId}/interactions/`, { params })
  }

  // ============================================================================
  // CONTACT NOTIFICATION PREFERENCES
  // ============================================================================

  /**
   * Get contact notification preferences
   */
  async getContactNotificationPreferences(contactId: number): Promise<ContactNotificationPreferences> {
    return this.request(`/api/crm/contacts/${contactId}/notification-preferences/`)
  }

  /**
   * Update contact notification preferences
   */
  async updateContactNotificationPreferences(
    contactId: number, 
    data: Partial<ContactNotificationPreferences>
  ): Promise<ContactNotificationPreferences> {
    return this.request(`/api/crm/contacts/${contactId}/notification-preferences/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  // ============================================================================
  // CONTACT SUBMISSION ASSIGNMENT
  // ============================================================================

  /**
   * Assign a contact submission to a user
   */
  async assignContactSubmission(uuid: string, userId: number): Promise<ContactSubmission> {
    return this.request(`/api/crm/contact-submissions/${uuid}/assign/`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId })
    })
  }
}